import Button from '@sdk/components/ui/buttons/Button';
import gettext from '@sdk/lang/gettext';
import { useSession } from '@sdk/session/SessionContext';
import { Feature } from '@sdk/common/features';

import { useNavigate } from '@web/Router/router.hooks';
import GroupsBlueSvg from '@web/assets/svg/Icons/48/groups-blue.svg';
import GroupsGraySvg from '@web/assets/svg/Icons/48/groups-gray.svg';
import UserBlueSvg from '@web/assets/svg/Icons/48/user-blue.svg';
import UserGraySvg from '@web/assets/svg/Icons/48/user-gray.svg';
import { ComposeConversationHeader } from '@web/conversations/ComposeConversation/ComposeConversationHeader/ComposeConversationHeader';
import { ActionsWrapper } from '@web/conversations/ComposeConversation/ComposeConversationActions/composeConversationActions.styles';
import {
  useConversationCreateRestrictions,
  useCreatePrivateConversationRedirect,
} from '@web/conversations/ComposeConversation/composeConversation.hooks';
import { NewConversationOption } from '@web/conversations/NewConversationScreen/NewConversationOption/NewConversationOption';
import { PremiumFeatureBanner } from '@web/components/PremiumFeatureBanner';
import PremiumGuard from '@web/premiumGuard/PremiumGuard';
import getPremiumTooltipId from '@web/premiumGuard/getPremiumTooltipId';
import { PremiumModalNames } from '@web/premiumGuard/constants/modalNames';
import PremiumGuardLabel from '@web/premiumGuard/PremiumGuardLabel';
import { useGroupConversationsPremiumGuard } from '@web/conversations/hooks/useGroupConversationsPremiumGuard';
import SecureSvg from '@web/assets/svg/Icons/16/secure.svg';
import { useGetConversations } from '@web/conversations/hooks/useGetConversations';

import {
  NewConversationScreenContainer,
  HeaderWrapper,
  Title,
  Divider,
  PremiumBadgeWrapper,
} from './newConversationScreen.styles';

export const NewConversationScreen = () => {
  const { currentUser } = useSession();
  const { isToastBilling } = currentUser.org;
  const navigate = useNavigate();
  const { data } = useGetConversations();
  const hasConversation = Boolean(data?.length);
  useCreatePrivateConversationRedirect();

  const {
    canStartPrivateConversation,
    canStartGroupConversation,
    canOnlyStartPrivateConversation,
  } = useConversationCreateRestrictions();

  const {
    areGroupConversationsEnabled,
    openPremiumGuardModal,
    premiumGuardConfig,
    tooltipContent,
    tooltipRefs,
  } = useGroupConversationsPremiumGuard();

  return (
    <>
      {currentUser.isManager && (
        <PremiumFeatureBanner
          feature={Feature.groupMessages}
          currentUser={currentUser}
          openFeatureModal={() => openPremiumGuardModal({ premiumFeatureName: 'groupMessages' })}
        />
      )}
      <NewConversationScreenContainer>
        <>
          <HeaderWrapper>
            <ComposeConversationHeader>
              <div>
                <ComposeConversationHeader.Title>
                  <Title>{gettext('New conversation')}</Title>
                </ComposeConversationHeader.Title>
                <ComposeConversationHeader.Subtitle>
                  {canOnlyStartPrivateConversation
                    ? gettext('Start a private conversation to communicate with your team.')
                    : gettext(
                        'Start a private or a group conversation to communicate with your team.'
                      )}
                </ComposeConversationHeader.Subtitle>
              </div>
            </ComposeConversationHeader>
          </HeaderWrapper>
          <Divider />

          {canStartGroupConversation && (
            <PremiumGuard
              hasAccess={areGroupConversationsEnabled}
              premiumFeature={Feature.groupMessages}
              premiumGuardModalName={PremiumModalNames.groupMessages}
              tooltipRef={tooltipRefs[getPremiumTooltipId(Feature.groupMessages)]}
              tooltipContent={!areGroupConversationsEnabled ? tooltipContent : ''}
              tooltipPlace="right">
              <NewConversationOption
                header={
                  <>
                    {gettext('Group conversation')}
                    {!areGroupConversationsEnabled && (
                      <PremiumBadgeWrapper>
                        {!isToastBilling ? (
                          <PremiumGuardLabel label={premiumGuardConfig.requiredPlan} />
                        ) : (
                          <SecureSvg />
                        )}
                      </PremiumBadgeWrapper>
                    )}
                  </>
                }
                description={gettext(
                  'Message multiple users, or even positions, locations, and groups.'
                )}
                icon={<GroupsGraySvg />}
                hoverIcon={<GroupsBlueSvg />}
                onClick={() => navigate.toConversationComposeGroup()}
              />
            </PremiumGuard>
          )}

          {canStartPrivateConversation && (
            <NewConversationOption
              header={<>{gettext('Private conversation')}</>}
              description={gettext('Start a private conversation with a coworker.')}
              icon={<UserGraySvg />}
              hoverIcon={<UserBlueSvg />}
              onClick={() => navigate.toConversationComposePrivate()}
            />
          )}

          {!canStartPrivateConversation && (
            <p>{gettext('You are not allowed to start new conversations.')}</p>
          )}
        </>

        {hasConversation && (
          <ActionsWrapper>
            <Button onClick={() => navigate.toConversationRoot()}>{gettext('Cancel')}</Button>
          </ActionsWrapper>
        )}
      </NewConversationScreenContainer>
    </>
  );
};
